/**
 * Proof bundle export — offline-verifiable artifacts.
 *
 * Exports two files that together constitute the offline proof bundle:
 *
 *   1. proof-bundle-<vehicleId>-<date>.json
 *      The complete ProofBundle as JSON. All cryptographic material is
 *      self-contained — no network calls needed to verify.
 *
 *   2. verify-offline.html
 *      A standalone single-file HTML + inline JS verifier. Re-runs the
 *      full chain integrity, JWS seal, and WebAuthn assertion checks
 *      with zero network calls and zero CDN dependencies. Drag-and-drop
 *      the JSON bundle onto the HTML page to verify.
 *
 * Canonical contract: gg-app CONTRACT.md (Lane B).
 * entryHash is re-derived from fields (no entryBytes CAS blobs needed).
 * Challenge: SHA-256(canonicalJSON({vehicleId, headHash, statement, createdAt})).
 *
 * Security notes:
 *   - All bundle fields interpolated into innerHTML are HTML-escaped.
 *   - The embedded JSON blob has '<' escaped as < so a '</script>'
 *     inside any field cannot break out of the <script> tag.
 *   - The offline verifier is self-contained (no CDN) — WebCrypto only.
 *
 * Capacitor note: file download via Blob + <a download> is the web path;
 * post-Capacitor-wrap this swaps to @capacitor/filesystem writeFile() in
 * the uploadPhoto pattern. The export functions are the abstraction boundary.
 */

import type { ProofBundle } from '@/types/domain';

// ---------------------------------------------------------------------------
// JSON bundle download
// ---------------------------------------------------------------------------

export function downloadProofBundleJson(bundle: ProofBundle): void {
  const json = JSON.stringify(bundle, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const date = new Date().toISOString().slice(0, 10);
  triggerDownload(url, `proof-bundle-${bundle.vehicleId}-${date}.json`);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// HTML escaping
// ---------------------------------------------------------------------------

/** Escape a string for safe use in innerHTML context. */
function htmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Serialize a value as JSON with '<' replaced by the u003c unicode escape.
 *
 * This prevents '</script>' inside any string field from breaking out of
 * a <script> tag in the generated HTML.
 */
function safeJsonForScript(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

// ---------------------------------------------------------------------------
// Standalone offline verifier HTML
// ---------------------------------------------------------------------------

export interface OfflineVerifierOptions {
  /**
   * WebAuthn origins the embedded verifier accepts. Callers should pass the
   * env-resolved deployment list (VITE_TRUST_ALLOWED_ORIGINS) — the exported
   * artifact is long-lived, so dev origins must not be baked into it.
   */
  allowedOrigins?: string[];
  /** RP IDs for rpIdHash validation — pass [VITE_TRUST_RP_ID]. */
  allowedRpIds?: string[];
}

/** Dev-only fallbacks; production exports pass env-resolved lists. */
const DEV_ALLOWED_ORIGINS = ['http://localhost', 'http://localhost:5173'];
const DEV_ALLOWED_RP_IDS = ['localhost'];

/**
 * Generate a standalone offline verifier HTML page with the bundle embedded.
 *
 * The verifier script is self-contained — no CDN requests, no `jose` library.
 * JWS ES256 is verified directly via WebCrypto SubtleCrypto.
 */
export function generateOfflineVerifierHtml(
  bundle: ProofBundle,
  options: OfflineVerifierOptions = {},
): string {
  // NOTE: the embedded <script> below intentionally duplicates crypto.ts
  // (canonicalJSON, derEcdsaToRaw, verifyJwsEs256, spkiPemFingerprint as
  // sealKeyFingerprint, etc.). The exported HTML must be fully self-contained
  // — no module imports — so it CANNOT reuse crypto.ts. Do not "deduplicate";
  // instead, mirror any behavioral change in crypto.ts into the inline copies
  // (bundle.test.ts pins the load-bearing markers).
  if (import.meta.env.PROD && (!options.allowedOrigins || !options.allowedRpIds)) {
    // Falling back to dev-only allowlists in a production build produces a
    // verifier that rejects every real attestation. Loud, not silent.
    console.warn(
      'generateOfflineVerifierHtml: missing allowedOrigins/allowedRpIds — dev-only fallbacks baked into exported verifier',
    );
  }
  const bundleJson = safeJsonForScript(bundle);
  const originsJson = safeJsonForScript(options.allowedOrigins ?? DEV_ALLOWED_ORIGINS);
  const rpIdsJson = safeJsonForScript(options.allowedRpIds ?? DEV_ALLOWED_RP_IDS);
  const exportedAt = htmlEscape(new Date().toISOString());
  const vehicleIdDisplay = htmlEscape(bundle.vehicleId);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Absolute Pinks — Proof Bundle Verifier</title>
<style>
  :root {
    --ink: #1a1a1a; --bg: #f3ead6; --card: #fbf7ef;
    --rule: #dbcfb4; --muted: #8a8470;
    --pink: #f3037e; --pink-wash: #fbe6ee;
    --good: #5e7a4f; --alert: #c0392b;
    --mono: 'IBM Plex Mono', Menlo, monospace;
    --sans: 'Source Sans 3', 'Helvetica Neue', Arial, sans-serif;
    --serif: 'Fraunces', Georgia, serif;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: var(--sans); background: var(--bg); color: var(--ink); padding: 32px 24px 80px; line-height: 1.55; }
  .page { max-width: 860px; margin: 0 auto; }
  h1 { font-family: var(--serif); font-size: 28px; font-weight: 500; margin-bottom: 6px; }
  .eyebrow { font-family: var(--mono); font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 12px; }
  .meta { font-size: 13px; color: var(--muted); margin-bottom: 32px; }
  .card { background: var(--card); border: 1.5px solid var(--rule); border-radius: 8px; padding: 20px 24px; margin-bottom: 16px; }
  .card h2 { font-size: 14px; font-weight: 700; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
  .status { display: inline-flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 20px; }
  .status.ok { background: color-mix(in srgb, var(--good) 12%, transparent); color: var(--good); border: 1px solid color-mix(in srgb, var(--good) 25%, transparent); }
  .status.fail { background: color-mix(in srgb, var(--alert) 12%, transparent); color: var(--alert); border: 1px solid color-mix(in srgb, var(--alert) 25%, transparent); }
  .reason { font-family: var(--mono); font-size: 12px; color: var(--alert); margin-top: 8px; white-space: pre-wrap; word-break: break-all; }
  .entry-row { display: flex; align-items: center; gap: 10px; font-size: 13px; padding: 6px 0; border-bottom: 1px solid var(--rule); }
  .entry-row:last-child { border-bottom: none; }
  .entry-id { font-family: var(--mono); font-size: 11px; color: var(--muted); flex: 1; }
  .summary { background: var(--pink-wash); border: 1.5px solid var(--rule); border-radius: 8px; padding: 20px 24px; margin-bottom: 32px; display: flex; align-items: center; gap: 16px; }
  .summary .big { font-family: var(--serif); font-size: 24px; font-weight: 500; }
  .note { font-size: 13px; color: var(--muted); margin-top: 24px; border-top: 1px solid var(--rule); padding-top: 16px; }
  button#verify-btn { background: var(--pink); color: #fff; border: none; border-radius: 4px; padding: 10px 20px; font-size: 14px; font-weight: 600; cursor: pointer; margin-top: 12px; }
  button#verify-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  #drop-zone { border: 2px dashed var(--rule); border-radius: 8px; padding: 32px; text-align: center; color: var(--muted); font-size: 14px; margin-bottom: 24px; cursor: pointer; }
  #drop-zone.drag-over { border-color: var(--pink); background: var(--pink-wash); }
  #file-input { display: none; }
</style>
</head>
<body>
<div class="page">
  <div class="eyebrow">Absolute Pinks · Offline Proof Bundle Verifier</div>
  <h1>Vehicle Provenance Verifier</h1>
  <p class="meta">
    Generated: ${exportedAt}<br>
    Vehicle: <span id="vehicle-id" style="font-family:var(--mono)">${vehicleIdDisplay}</span>
  </p>

  <div id="drop-zone" onclick="document.getElementById('file-input').click()"
       ondragover="event.preventDefault();this.classList.add('drag-over')"
       ondragleave="this.classList.remove('drag-over')"
       ondrop="handleDrop(event)">
    Drop a proof-bundle JSON file here to verify, or click to browse.<br>
    <small style="color:var(--muted)">Or use the embedded bundle below.</small>
  </div>
  <input type="file" id="file-input" accept=".json,application/json" onchange="handleFileInput(event)">

  <button id="verify-btn" onclick="runVerification()">Verify embedded bundle</button>

  <div id="results" style="margin-top:32px"></div>

  <p class="note">
    This verifier runs entirely in your browser with no data sent to any server
    and no external resources loaded. Chain integrity and all signature checks
    use the browser built-in WebCrypto API (SHA-256, ECDSA P-256).
    <br><br>
    <strong>Key pinning note:</strong> both public keys in this bundle originate from
    Absolute Pinks. A forged bundle can embed its own keys and self-verify, so
    independent verification requires comparing the fingerprint shown below
    against the published fingerprint at
    <a href="https://absolutepinks.com/.well-known/seal-key-fingerprint.txt" target="_blank" rel="noopener noreferrer">
    absolutepinks.com/.well-known/seal-key-fingerprint.txt</a>.
    <br><br>
    <strong>Seal key fingerprint</strong> (SHA-256 of the SPKI DER):<br>
    <span id="seal-fingerprint" style="font-family:var(--mono);word-break:break-all">computing…</span>
  </p>
</div>

<script type="module">
// Embedded bundle (< is unicode-escaped to prevent script-tag breakout).
const EMBEDDED_BUNDLE = ${bundleJson};

// Allowed origins / RP IDs — injected at export time from the deployment's
// env (VITE_TRUST_ALLOWED_ORIGINS / VITE_TRUST_RP_ID), must match gg-app.
const ALLOWED_ORIGINS = ${originsJson};
const ALLOWED_RP_IDS = ${rpIdsJson};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
function htmlEscape(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function decodeBase64url(b64url) {
  const padded = b64url + '==='.slice(0, (4 - (b64url.length % 4)) % 4);
  const b64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function decodeBase64(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function encodeBase64url(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\\+/g, '-').replace(/\\//g, '_').replace(/=+$/,'');
}
function toHex(buf) {
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,'0')).join('');
}
async function sha256Hex(bytes) {
  return toHex(await crypto.subtle.digest('SHA-256', bytes));
}
async function sha256Raw(bytes) {
  return crypto.subtle.digest('SHA-256', bytes);
}

// ---------------------------------------------------------------------------
// Canonical JSON
// ---------------------------------------------------------------------------
function canonicalJSON(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonicalJSON).join(',') + ']';
  const sorted = Object.keys(value).sort().map(k => JSON.stringify(k) + ':' + canonicalJSON(value[k]));
  return '{' + sorted.join(',') + '}';
}

// ---------------------------------------------------------------------------
// DER ECDSA → raw r‖s (64 bytes)
// ---------------------------------------------------------------------------
function derEcdsaToRaw(der) {
  let offset = 0;
  if (der[offset++] !== 0x30) throw new Error('DER: expected SEQUENCE (0x30)');
  let seqLen = der[offset++];
  if (seqLen & 0x80) {
    const n = seqLen & 0x7f;
    seqLen = 0;
    for (let i = 0; i < n; i++) seqLen = (seqLen << 8) | der[offset++];
  }
  const seqEnd = offset + seqLen;
  if (seqEnd > der.length) throw new Error('DER: SEQUENCE length exceeds buffer');

  function readInt() {
    if (offset >= seqEnd) throw new Error('DER: INTEGER read past sequence boundary');
    if (der[offset++] !== 0x02) throw new Error('DER: expected INTEGER (0x02)');
    const len = der[offset++];
    if (len === 0 || len > 33) throw new Error('DER: INTEGER length ' + len + ' invalid for P-256');
    if (offset + len > seqEnd) throw new Error('DER: INTEGER length exceeds SEQUENCE boundary');
    let start = offset, actualLen = len;
    while (actualLen > 32 && der[start] === 0x00) { start++; actualLen--; }
    if (actualLen > 32) throw new Error('DER: INTEGER actual length ' + actualLen + ' exceeds 32');
    const out = new Uint8Array(32);
    out.set(der.subarray(start, start + actualLen), 32 - actualLen);
    offset += len;
    return out;
  }
  const r = readInt(), s = readInt();
  // Strict DER: nothing may follow s (parity with crypto.ts derEcdsaToRaw).
  if (offset !== seqEnd || seqEnd !== der.length) {
    throw new Error('DER: trailing bytes after SEQUENCE');
  }
  const raw = new Uint8Array(64);
  raw.set(r, 0); raw.set(s, 32);
  return raw;
}

// ---------------------------------------------------------------------------
// SPKI PEM → CryptoKey
// ---------------------------------------------------------------------------
async function importSpkiPem(pem) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\\s+/g, '');
  return crypto.subtle.importKey('spki', decodeBase64(b64),
    { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
}

// ---------------------------------------------------------------------------
// JWS ES256 verification (pure WebCrypto)
// ---------------------------------------------------------------------------
async function verifyJwsEs256(jws, publicKey) {
  const parts = jws.split('.');
  if (parts.length !== 3) throw new Error('JWS: expected 3 compact parts');
  const [headerB64, payloadB64, sigB64] = parts;
  // Header validation — parity with crypto.ts: wrong-alg tokens must fail
  // loudly, unknown crit extensions are rejected (RFC 7515 §4.1.11).
  let header;
  try {
    header = JSON.parse(new TextDecoder().decode(decodeBase64url(headerB64)));
  } catch (e) {
    throw new Error('JWS: protected header is not valid JSON');
  }
  if (typeof header !== 'object' || header === null) throw new Error('JWS: protected header must be a JSON object');
  if (header.alg !== 'ES256') throw new Error('JWS: alg must be "ES256", got "' + String(header.alg) + '"');
  if (header.crit !== undefined) throw new Error('JWS: crit header extensions are not supported');
  const signingInput = headerB64 + '.' + payloadB64;
  const signingBytes = new TextEncoder().encode(signingInput);
  const sigBytes = decodeBase64url(sigB64);
  if (sigBytes.length !== 64) throw new Error('JWS: signature must be 64 bytes (raw r‖s), got ' + sigBytes.length);
  const valid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' }, publicKey, sigBytes, signingBytes);
  if (!valid) throw new Error('JWS: signature verification failed');
  const payloadText = new TextDecoder().decode(decodeBase64url(payloadB64));
  return JSON.parse(payloadText);
}

// ---------------------------------------------------------------------------
// Attestation challenge
// ---------------------------------------------------------------------------
async function computeChallenge(vehicleId, headHash, statement, createdAt) {
  const canonical = canonicalJSON({ vehicleId, headHash, statement, createdAt });
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
  return encodeBase64url(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// Chain integrity
// ---------------------------------------------------------------------------
// NOTE: results carry RAW strings; escaping happens once, in renderResults.
async function verifyChain(entries) {
  const results = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry.seq !== i) {
      results.push({ entryId: entry.id, ok: false, reason: 'seq mismatch: ' + entry.seq + ' expected ' + i });
      continue;
    }
    const forHashing = { ...entry, entryHash: '' };
    const computed = await sha256Hex(new TextEncoder().encode(canonicalJSON(forHashing)));
    if (computed !== entry.entryHash) {
      results.push({ entryId: entry.id, ok: false, reason: 'tamperedEntry: entryHash mismatch' });
      continue;
    }
    if (i === 0 && entry.prevHash !== null) {
      results.push({ entryId: entry.id, ok: false, reason: 'forkDetected: genesis prevHash must be null' });
      continue;
    }
    if (i > 0 && entry.prevHash !== entries[i-1].entryHash) {
      results.push({ entryId: entry.id, ok: false, reason: 'forkDetected: prevHash chain broken' });
      continue;
    }
    results.push({ entryId: entry.id, ok: true });
  }
  return results;
}

// ---------------------------------------------------------------------------
// AP seal (JWS ES256 via WebCrypto)
// ---------------------------------------------------------------------------
async function verifySeal(seal, sealPublicKey, headHash) {
  try {
    const key = await importSpkiPem(sealPublicKey);
    const payload = await verifyJwsEs256(seal.jws, key);
    if (payload.headHash !== headHash) {
      return { ok: false, reason: 'sealHeadMismatch: headHash mismatch in JWS payload' };
    }
    return { ok: true };
  } catch(e) {
    return { ok: false, reason: 'forgedSeal: ' + String(e) };
  }
}

// SHA-256 fingerprint of the seal public key's SPKI DER, colon-separated hex.
// Compare against absolutepinks.com/.well-known/seal-key-fingerprint.txt.
async function sealKeyFingerprint(pem) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\\s+/g, '');
  const der = decodeBase64(b64);
  if (der.length === 0) throw new Error('empty PEM body');
  const hex = toHex(await sha256Raw(der));
  return hex.match(/.{2}/g).join(':');
}

// ---------------------------------------------------------------------------
// WebAuthn assertion verification
// ---------------------------------------------------------------------------
async function verifyAssertion(att, attesterPublicKey) {
  try {
    const authData = decodeBase64url(att.authenticatorData);
    const clientDataJSON = decodeBase64url(att.clientDataJSON);
    const sigDer = decodeBase64url(att.signature);

    // UV + UP flags.
    if (authData.length < 37) return { ok: false, reason: 'authenticatorData too short' };
    const flags = authData[32];
    if ((flags & 0x04) === 0) return { ok: false, reason: 'userVerificationAbsent: UV flag not set' };
    if ((flags & 0x01) === 0) return { ok: false, reason: 'userPresenceAbsent: UP flag not set' };

    // DER → raw r‖s.
    const sigRaw = derEcdsaToRaw(sigDer);

    // Parse and validate clientDataJSON.
    const clientData = JSON.parse(new TextDecoder().decode(clientDataJSON));
    if (clientData.type !== 'webauthn.get') {
      return { ok: false, reason: 'clientDataJSON.type must be webauthn.get, got: ' + String(clientData.type) };
    }
    if (!ALLOWED_ORIGINS.includes(clientData.origin)) {
      return { ok: false, reason: 'originNotAllowed: "' + String(clientData.origin) + '" not in allowlist' };
    }

    // rpIdHash check: try all allowed RP IDs.
    let rpIdOk = false;
    for (const rpId of ALLOWED_RP_IDS) {
      const expectedHash = new Uint8Array(await sha256Raw(new TextEncoder().encode(rpId)));
      const storedHash = authData.slice(0, 32);
      if (expectedHash.every((b, i) => b === storedHash[i])) { rpIdOk = true; break; }
    }
    if (!rpIdOk) return { ok: false, reason: 'rpIdHash mismatch: authenticatorData[0..32] does not match any allowed rpId' };

    // Challenge check.
    const embeddedChallenge = clientData.challenge;
    const expected = await computeChallenge(att.vehicleId, att.headHash, att.statement, att.createdAt);
    if (embeddedChallenge !== expected) {
      return { ok: false, reason: 'challengeMismatch: challenge does not match expected' };
    }

    // Import SPKI DER base64 (standard).
    const pubkey = await crypto.subtle.importKey('spki', decodeBase64(attesterPublicKey),
      { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);

    // signed = authData ‖ sha256(clientDataJSON).
    const clientHash = await sha256Raw(clientDataJSON);
    const signed = new Uint8Array(authData.length + 32);
    signed.set(authData);
    signed.set(new Uint8Array(clientHash), authData.length);

    const valid = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' }, pubkey, sigRaw, signed);
    return valid ? { ok: true } : { ok: false, reason: 'forgedAttestation: ECDSA signature invalid' };
  } catch(e) {
    return { ok: false, reason: String(e) };
  }
}

// ---------------------------------------------------------------------------
// Render results
// ---------------------------------------------------------------------------
function statusBadge(ok) {
  const cls = ok ? 'ok' : 'fail';
  const txt = ok ? 'PASS' : 'FAIL';
  return '<span class="status ' + cls + '">' + txt + '</span>';
}

function renderResults(bundle, chainResults, sealResult, attestResult, headOk) {
  const el = document.getElementById('results');
  const attOk = attestResult === null ? true : attestResult.ok;
  const allOk = chainResults.every(r => r.ok) && sealResult.ok && attOk && headOk.ok;

  // vehicleId and headHash are data from the bundle — escape for innerHTML.
  const safeVehicleId = htmlEscape(bundle.vehicleId);
  const safeHeadHash = htmlEscape(bundle.headHash);

  let html = '<div class="summary"><div class="big">' +
    (allOk ? '&#10003; Verified' : '&#10007; Verification failed') +
    '</div><div style="font-size:13px;color:var(--muted)">Vehicle: <code>' +
    safeVehicleId + '</code><br>Head: <code style="word-break:break-all">' +
    safeHeadHash + '</code></div></div>';

  // Chain integrity card.
  const chainOk = chainResults.every(r => r.ok);
  html += '<div class="card"><h2>Chain integrity ' + statusBadge(chainOk) + '</h2>';
  for (const r of chainResults) {
    html += '<div class="entry-row"><span class="entry-id">' +
      htmlEscape(r.entryId) + '</span>' + statusBadge(r.ok);
    if (!r.ok) html += '<span class="reason">' + htmlEscape(r.reason) + '</span>';
    html += '</div>';
  }
  html += '</div>';

  // Seal card.
  html += '<div class="card"><h2>AP KMS seal ' + statusBadge(sealResult.ok) + '</h2>';
  if (!sealResult.ok) html += '<div class="reason">' + htmlEscape(sealResult.reason) + '</div>';
  html += '</div>';

  // Head consistency card.
  html += '<div class="card"><h2>Head consistency ' + statusBadge(headOk.ok) + '</h2>';
  if (!headOk.ok) html += '<div class="reason">' + htmlEscape(headOk.reason) + '</div>';
  html += '</div>';

  // Attestation card.
  if (bundle.attestation) {
    const safeCredId = htmlEscape(bundle.attestation.attesterCredId);
    const safeStatement = htmlEscape(bundle.attestation.statement);
    html += '<div class="card"><h2>Attestation: ' + safeCredId + ' ' + statusBadge(attestResult?.ok ?? false) + '</h2>';
    html += '<div style="font-size:13px;color:var(--muted)">' + safeStatement + '</div>';
    if (!attestResult?.ok) {
      html += '<div class="reason">' + htmlEscape(attestResult?.reason ?? 'unknown') + '</div>';
    }
    html += '</div>';
  } else {
    html += '<div class="card"><h2>Attestations</h2><p style="font-size:13px;color:var(--muted)">No attestation in this bundle.</p></div>';
  }

  el.innerHTML = html;
}

// ---------------------------------------------------------------------------
// Main verify flow
// ---------------------------------------------------------------------------
async function verify(bundle) {
  const btn = document.getElementById('verify-btn');
  btn.disabled = true;
  btn.textContent = 'Verifying…';
  document.getElementById('results').innerHTML = '<p style="color:var(--muted);font-size:14px">Running verification…</p>';

  try {
    const chainResults = await verifyChain(bundle.entries);
    const sealResult = await verifySeal(bundle.seal, bundle.sealPublicKey, bundle.headHash);
    const lastEntry = bundle.entries[bundle.entries.length - 1];
    const headOk = lastEntry && lastEntry.entryHash === bundle.headHash
      ? { ok: true }
      : { ok: false, reason: 'headMismatch: bundle.headHash does not match last entry entryHash' };

    // Attestation must be bound to THIS bundle: the assertion challenge only
    // commits to the attestation's own fields, so a genuine attestation from
    // another vehicle/chain would otherwise verify green when transplanted.
    let attestResult = null;
    if (bundle.attestation) {
      const att = bundle.attestation;
      if (!bundle.attesterPublicKey) {
        attestResult = { ok: false, reason: 'bundle has attestation but attesterPublicKey is missing' };
      } else if (att.vehicleId !== bundle.vehicleId) {
        attestResult = { ok: false, reason: 'attestationVehicleMismatch: attestation is for vehicle "' + att.vehicleId + '", bundle is for "' + bundle.vehicleId + '"' };
      } else if (!bundle.entries.some(en => en.entryHash === att.headHash)) {
        attestResult = { ok: false, reason: 'attestationHeadUnknown: attestation headHash does not match any entry in this chain' };
      } else {
        attestResult = await verifyAssertion(att, bundle.attesterPublicKey);
      }
    }

    renderResults(bundle, chainResults, sealResult, attestResult, headOk);

    // Seal key fingerprint — shown so the "compare out-of-band" instruction
    // in the note is actually actionable.
    try {
      document.getElementById('seal-fingerprint').textContent =
        await sealKeyFingerprint(bundle.sealPublicKey);
    } catch(e) {
      document.getElementById('seal-fingerprint').textContent =
        'unavailable (' + String(e) + ')';
    }
  } catch(e) {
    const el = document.getElementById('results');
    el.innerHTML = '<div class="card"><p class="reason">Unexpected error: ' +
      htmlEscape(String(e)) + '</p></div>';
  } finally {
    btn.disabled = false;
    btn.textContent = 'Verify embedded bundle';
  }
}

window.runVerification = () => verify(EMBEDDED_BUNDLE);
window.handleDrop = async (e) => {
  e.preventDefault();
  e.currentTarget.classList.remove('drag-over');
  const file = e.dataTransfer.files[0];
  if (!file) return;
  try {
    verify(JSON.parse(await file.text()));
  } catch(e) {
    document.getElementById('results').innerHTML =
      '<div class="card"><p class="reason">Failed to parse JSON: ' + htmlEscape(String(e)) + '</p></div>';
  }
};
window.handleFileInput = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  try {
    verify(JSON.parse(await file.text()));
  } catch(e) {
    document.getElementById('results').innerHTML =
      '<div class="card"><p class="reason">Failed to parse JSON: ' + htmlEscape(String(e)) + '</p></div>';
  }
};

verify(EMBEDDED_BUNDLE);
</script>
</body>
</html>`;
}

export function downloadOfflineVerifier(
  bundle: ProofBundle,
  options: OfflineVerifierOptions = {},
): void {
  const html = generateOfflineVerifierHtml(bundle, options);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `verify-offline-${bundle.vehicleId}.html`);
  URL.revokeObjectURL(url);
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
